import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TableColumnReorderEvent, TableModule, TablePageEvent } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { EMPTY_PATIENT_PAGE_MODEL, PatientModel, PatientPageModel } from '../../global-data-models/patient.model';
import { DEFAULT_PATIENT_PAGE_OPTIONS, OrderDir, PatientPageOptions } from '../../global-services/patient.service';
import { PatientsViewFacade } from './patients-view.facade';
import { Subscription } from 'rxjs';
import { DatePipe, JsonPipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { CrudPatientComponent } from '../crud-patient/crud-patient.component';
import { CrudPatientMode } from '../crud-patient/crud-patient.facade';
import { MeService } from '../../global-services/me.service';
import { MeModel } from '../../global-data-models/me.model';
import { Router } from '@angular/router';
import { PatientActionsMenuComponent, ActionMenuEvent } from '../../global-components/patient-actions-menu/patient-actions-menu.component';
import { PatientsMigrateComponent } from '../patients-migrate/patients-migrate.component';

export interface TableConfig {
  limit: number;
  rowsPerPageOptions: number[];
  orderDir: OrderDir;
}

@Component({
  standalone: true,
  selector: 'app-patients-view',
  imports: [...COMMON_IMPORTS, TableModule, PaginatorModule, DialogModule, PatientActionsMenuComponent, PatientsMigrateComponent],
  templateUrl: './patients-view.component.html',
})
export class PatientsViewComponent implements OnInit, OnDestroy {
  public readonly CrudPatientMode = CrudPatientMode;
  public readonly OrderDir = OrderDir;

  @Input() orgId!: string;

  public patientPage: PatientPageModel = EMPTY_PATIENT_PAGE_MODEL;
  public pageOptions: PatientPageOptions = DEFAULT_PATIENT_PAGE_OPTIONS;

  public showUpdatePatientDialog: boolean = false;
  public updatePatientId?: string;
  private uiStateSub?: Subscription;
  private searchSub?: Subscription;
  public showMigrationPopup: boolean = false;

  public readonly searchControl = new FormControl('');

  public constructor(
    private readonly facade: PatientsViewFacade,
    private readonly meService: MeService,
    private readonly router: Router,
  ) {
    this.setupSearchSubscription();
  }

  public async ngOnInit() {
    this.uiStateSub = this.facade.uiState$.subscribe((state) => {
      this.patientPage = state.patientPage;
    });

    this.updatePageOption('orgId', this.orgId);
    await this.facade.loadPatients();

    //Handle migration popup
    const me: MeModel | null = this.meService.getLastMeEmitted();
    if (me?.canMigrate && !me.didMigrate && !me.didSkipMigration) {
      this.showMigrationPopup = true;
    }
  }

  private updatePageOption(key: string, value: any) {
    this.pageOptions = {
      ...this.pageOptions,
      [key]: value,
    };
    this.facade.setPatientPageOptions(this.pageOptions);
  }

  private setupSearchSubscription() {
    this.searchSub = this.searchControl.valueChanges
      .pipe(
        debounceTime(300), // 300ms debounce
        distinctUntilChanged(),
      )
      .subscribe(async (searchTerm: string | null) => {
        this.updatePageOption('search', searchTerm || '');
        await this.facade.loadPatients();
      });
  }

  public async onPageEvent(event: TablePageEvent) {
    this.updatePageOption('limit', event.rows);
    this.updatePageOption('page', event.first / event.rows + 1);
    await this.facade.loadPatients();
  }

  public async onSortEvent(event: any) {
    this.updatePageOption('orderDir', event.order === 1 ? OrderDir.ASC : OrderDir.DESC);
    this.updatePageOption('orderBy', event.field);
    await this.facade.loadPatients();
  }

  public async onCardSort(field: string) {
    const newOrder = this.pageOptions.orderBy === field && this.pageOptions.orderDir === OrderDir.ASC ? OrderDir.DESC : OrderDir.ASC;

    this.updatePageOption('orderDir', newOrder);
    this.updatePageOption('orderBy', field);
    await this.facade.loadPatients();
  }

  public async onPaginatorPageEvent(event: any) {
    this.updatePageOption('limit', event.rows);
    this.updatePageOption('page', (event.first || 0) / (event.rows || 1) + 1);
    await this.facade.loadPatients();
  }

  public async reload(): Promise<void> {
    await this.facade.loadPatients();
  }

  public startUpdatePatient(patientId: string) {
    this.updatePatientId = patientId;
    this.showUpdatePatientDialog = true;
  }

  public stopUpdatePatient() {
    this.showUpdatePatientDialog = false;
    this.updatePatientId = undefined;
  }

  public editPatient(patientId: string) {
    const userId = this.meService.getLastMeEmitted()?.id;
    console.log('Edit Patient clicked for patient:', patientId, 'by user:', userId, 'in org:', this.orgId);
    this.startUpdatePatient(patientId);
  }

  public viewPatient(patientId: string) {
    this.router.navigate(['/orgs', this.orgId, 'patients', patientId, 'manage']);
  }

  public getAge(dateOfBirth: string): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  public onMenuEvent(event: ActionMenuEvent): void {
    if (event === ActionMenuEvent.PATIENT_DELETED) {
      this.reload();
    }
  }

  public async closeMigrationDialog(): Promise<void> {
    this.showMigrationPopup = false;
    await this.meService.refreshMe();
    this.reload();
  }

  public ngOnDestroy(): void {
    this.uiStateSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.facade.destroy();
  }
}
